import { useState } from 'react';
import { submitContactForm } from '../services/api';
import { validateEmail, validatePhone, validateName, validateMessage } from '../utils/validation';

export function ContactForm({onSuccess}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const validateForm = (data) => {
    const newErrors = {};

    // Name validation
    if (!validateName(data.name)) {
      newErrors.name = !data.name.trim() ? 'Name is required' : 'Name must be less than 100 characters';
    }

    // Email validation 
    if (!data.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(data.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional)
    if (data.phone && !validatePhone(data.phone)) {
      newErrors.phone = data.phone.length > 20 
        ? 'Phone number must be less than 20 characters'
        : 'Please enter a valid phone number';
    }

    // Message validation
    if (!validateMessage(data.message)) {
      newErrors.message = !data.message.trim() ? 'Message is required' : 'Message must be less than 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    // Get form data
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name')?.trim(),
      email: formData.get('email')?.trim(),
      phone: formData.get('phone')?.trim(),
      message: formData.get('message')?.trim()
    };

    // Validate form
    if (!validateForm(data)) {
      setIsSubmitting(false);
      return;
    }

    try {
      await submitContactForm(data);
      onSuccess();
      e.target.reset();
      setErrors({});
    } catch (error) {
      console.error('Contact form error:', error);
      if (error.response?.status === 429) {
        setSubmitError('Too many attempts. Please try again later.');
      } else if (error.response?.status === 400) {
        setSubmitError('Please check your input and try again.');
      } else {
        setSubmitError('An error occurred while sending your message. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const FormField = ({ label, id, type = 'text', required = false, maxLength, ...props }) => (
    <div className="mb-6">
      <label htmlFor={id} className="block text-brand-dark font-semibold mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          name={id}
          required={required}
          maxLength={maxLength}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand
            ${errors[id] ? 'border-red-500 ring-red-500' : 'border-gray-300'}`}
          disabled={isSubmitting}
          rows="4"
          aria-invalid={errors[id] ? 'true' : 'false'}
          aria-describedby={errors[id] ? `${id}-error` : undefined}
          {...props}
        />
      ) : (
        <input
          type={type}
          id={id}
          name={id}
          required={required}
          maxLength={maxLength}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand
            ${errors[id] ? 'border-red-500 ring-red-500' : 'border-gray-300'}`}
          disabled={isSubmitting}
          aria-invalid={errors[id] ? 'true' : 'false'}
          aria-describedby={errors[id] ? `${id}-error` : undefined}
          {...props}
        />
      )}
      {errors[id] && (
        <p id={`${id}-error`} className="mt-1 text-red-500 text-sm" role="alert">
          {errors[id]}
        </p>
      )}
    </div>
  );

  return (
    <>
        <form 
          className="w-full max-w-lg bg-white rounded-lg shadow-xl p-8"
          onSubmit={handleSubmit}
          noValidate
        >
          {submitError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
              <p className="text-red-600">{submitError}</p>
            </div>
          )}

          <FormField
            label="Name"
            id="name"
            required
            maxLength={100}
            placeholder="Your name"
            autoComplete="name"
          />

          <FormField
            label="Email"
            id="email"
            type="email"
            required
            maxLength={100}
            placeholder="your.email@example.com"
            autoComplete="email"
          />

          <FormField
            label="Phone Number"
            id="phone"
            type="tel"
            maxLength={20}
            placeholder="(123) 456-7890"
            autoComplete="tel"
          />

          <FormField
            label="Additional Information"
            id="message"
            type="textarea"
            required
            maxLength={1000}
            placeholder="Tell us about your project or any specific requirements..."
          />

          <button
            type="submit"
            className="w-full bg-brand text-white py-3 px-6 rounded-lg font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Get in Touch'}
          </button>
        </form>
    </>
  );
}